import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChatTeardropDots,
  PaperPlaneTilt,
  CircleNotch,
  Briefcase,
  File,
  MagnifyingGlass,
  Plus,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/features/auth/store';
import {
  fetchMyThreads,
  fetchMyThreadMessages,
  sendMyThreadMessage,
  markMyThreadRead,
  downloadSolicitorDocument,
  type PortalThread,
  type PortalMessage,
} from '@/lib/solicitorPortalApi';
import { StartConversationModal } from '@/components/solicitor/StartConversationModal';
import { getApiErrorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { onMessage, useWebSocketStore, joinThread, leaveThread } from '@/lib/websocket';

const POLL_INTERVAL_MS = 30_000; // safety-net only — WS push handles real-time

export default function MessagesPage() {
  const currentUser = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlThreadId = searchParams.get('thread');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(urlThreadId);
  const [searchQuery, setSearchQuery] = useState('');
  const [startOpen, setStartOpen] = useState(false);
  const qc = useQueryClient();
  const wsConnected = useWebSocketStore((s) => s.connected);

  // URL <-> selection sync.
  //
  // These two effects used to depend on each other's output: one listed `selectedThreadId`,
  // the other listed `searchParams`, and setSearchParams hands back a NEW object every time.
  // So selecting a thread wrote the URL, the new URL re-ran the first effect, and React hit
  // "Maximum update depth exceeded" before it settled. Each now depends only on its own
  // input, and the URL is read live rather than through a captured value that can go stale.

  // URL -> selection: reacts to the address bar only.
  useEffect(() => {
    if (urlThreadId) setSelectedThreadId((cur) => (cur === urlThreadId ? cur : urlThreadId));
  }, [urlThreadId]);

  // Selection -> URL: reacts to the selection only.
  useEffect(() => {
    if (!selectedThreadId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('thread') === selectedThreadId) return;
    params.set('thread', selectedThreadId);
    setSearchParams(params, { replace: true });
  }, [selectedThreadId, setSearchParams]);

  // WS: when any new message arrives, refresh the thread list so unread badges update.
  useEffect(() => {
    const unsub = onMessage(() => {
      qc.invalidateQueries({ queryKey: ['portal', 'threads'] });
    });
    return unsub;
  }, [wsConnected, qc]);

  const threadsQ = useQuery({
    queryKey: ['portal', 'threads'],
    queryFn: () => fetchMyThreads(),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    enabled: Boolean(currentUser),
  });

  const filteredThreads = useMemo(() => {
    if (!threadsQ.data) return [];
    if (!searchQuery.trim()) return threadsQ.data;
    const q = searchQuery.toLowerCase();
    return threadsQ.data.filter((t) => {
      const haystack = [
        t.subject,
        t.case?.caseNumber,
        t.case?.caseName,
        ...t.participants.map((p) => `${p.user.firstName} ${p.user.lastName}`),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [threadsQ.data, searchQuery]);

  // Auto-select first thread
  useEffect(() => {
    if (selectedThreadId) return;
    const first = threadsQ.data?.[0];
    if (first) setSelectedThreadId(first.id);
  }, [threadsQ.data, selectedThreadId]);

  const selectedThread = useMemo(
    () => threadsQ.data?.find((t) => t.id === selectedThreadId) ?? null,
    [threadsQ.data, selectedThreadId],
  );

  if (!currentUser) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">Loading session...</Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Messages</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Communicate with chambers about your matters
        </p>
      </div>

      <div className="grid h-[calc(100vh-280px)] min-h-[480px] grid-cols-[320px_1fr] gap-4">
        {/* Thread list */}
        <Card className="flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <ChatTeardropDots size={18} className="text-brand-600 dark:text-brand-400" weight="duotone" />
            <h2 className="text-sm font-semibold tracking-tight">Conversations</h2>
            {threadsQ.data && (
              <span className="text-xs text-muted-foreground">({threadsQ.data.length})</span>
            )}
            <Button
              size="sm"
              variant="outline"
              aria-label="Start a conversation with chambers"
              className="ml-auto h-7 gap-1 px-2 text-xs"
              onClick={() => setStartOpen(true)}
            >
              <Plus size={13} weight="bold" />
              New
            </Button>
          </div>

          <div className="border-b border-border px-3 py-2">
            <div className="relative">
              <MagnifyingGlass
                size={14}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {threadsQ.isLoading && (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <CircleNotch size={14} className="animate-spin" />
                Loading...
              </div>
            )}
            {threadsQ.isError && (
              <div className="p-4 text-sm text-red-600 dark:text-red-400">
                Could not load conversations. {getApiErrorMessage(threadsQ.error)}
              </div>
            )}
            {threadsQ.data && threadsQ.data.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <ChatTeardropDots
                  size={32}
                  weight="duotone"
                  className="mx-auto mb-2 text-muted-foreground/40"
                />
                <p>No conversations yet.</p>
                <Button size="sm" className="mt-3 gap-1" onClick={() => setStartOpen(true)}>
                  <Plus size={13} weight="bold" />
                  Message chambers
                </Button>
              </div>
            )}
            {filteredThreads.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                isSelected={t.id === selectedThreadId}
                currentUserId={currentUser.id}
                onSelect={() => setSelectedThreadId(t.id)}
              />
            ))}
          </div>
        </Card>

        {/* Thread content */}
        {selectedThread ? (
          <ThreadPane key={selectedThread.id} thread={selectedThread} />
        ) : (
          <Card className="flex flex-1 items-center justify-center text-center text-sm text-muted-foreground">
            <div>
              <ChatTeardropDots
                size={40}
                weight="duotone"
                className="mx-auto mb-2 text-muted-foreground/40"
              />
              Select a conversation
            </div>
          </Card>
        )}
      </div>

      <StartConversationModal
        open={startOpen}
        onClose={() => setStartOpen(false)}
        onCreated={(threadId) => setSelectedThreadId(threadId)}
      />
    </div>
  );
}

function ThreadRow({
  thread,
  isSelected,
  currentUserId,
  onSelect,
}: {
  thread: PortalThread;
  isSelected: boolean;
  currentUserId: string;
  onSelect: () => void;
}) {
  const me = thread.participants.find((p) => p.userId === currentUserId);
  const isUnread =
    me &&
    thread.lastMessageAt &&
    new Date(thread.lastMessageAt) > new Date(me.joinedAt) &&
    me.lastReadSeq === 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-accent/40',
        isSelected && 'bg-brand-50/70 dark:bg-brand-950/20',
      )}
    >
      <div
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold uppercase',
          isSelected
            ? 'bg-brand-600 text-white'
            : 'bg-brand-100 text-brand-700 dark:bg-brand-950 dark:text-brand-300',
        )}
      >
        <Briefcase size={14} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div
            className={cn(
              'truncate text-sm',
              isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground',
            )}
          >
            {thread.subject || thread.case?.caseName || 'Conversation'}
          </div>
          {thread.lastMessageAt && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatRelative(thread.lastMessageAt)}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          {thread.case && (
            <span className="truncate">{thread.case.caseNumber}</span>
          )}
          {isUnread && (
            <span className="ml-auto flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold leading-none text-white">
              New
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function ThreadPane({ thread }: { thread: PortalThread }) {
  const currentUser = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const wsConnected = useWebSocketStore((s) => s.connected);
  const [composer, setComposer] = useState('');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Join the thread room for real-time push; leave on unmount or thread change.
  useEffect(() => {
    joinThread(thread.id);
    return () => { leaveThread(thread.id); };
  }, [thread.id, wsConnected]);

  // WS: invalidate messages query when a new message arrives in this thread.
  useEffect(() => {
    const unsub = onMessage((evt) => {
      if (evt.threadId === thread.id) {
        qc.invalidateQueries({ queryKey: ['portal', 'messages', thread.id] });
        qc.invalidateQueries({ queryKey: ['portal', 'threads'] });
      }
    });
    return unsub;
  }, [thread.id, wsConnected, qc]);

  const messagesQ = useQuery({
    queryKey: ['portal', 'messages', thread.id],
    queryFn: () => fetchMyThreadMessages(thread.id),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesQ.data?.length]);

  useEffect(() => {
    const last = messagesQ.data?.at(-1);
    if (!last) return;
    markMyThreadRead(thread.id, last.sequence).catch(() => undefined);
  }, [thread.id, messagesQ.data]);

  const sendMut = useMutation({
    mutationFn: (text: string) => sendMyThreadMessage(thread.id, text),
    onSuccess: () => {
      setComposer('');
      qc.invalidateQueries({ queryKey: ['portal', 'messages', thread.id] });
      qc.invalidateQueries({ queryKey: ['portal', 'threads'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not send message')),
  });

  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = composer.trim();
    if (!text || sendMut.isPending) return;
    sendMut.mutate(text);
  };

  const otherParticipants = thread.participants.filter(
    (p) => p.userId !== currentUser?.id,
  );

  return (
    <Card className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {thread.subject || thread.case?.caseName || 'Conversation'}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {otherParticipants.length === 0
              ? 'No other participants'
              : otherParticipants.map((p) => `${p.user.firstName} ${p.user.lastName}`).join(' · ')}
          </div>
        </div>
        {thread.case && (
          <Badge variant="outline" className="shrink-0 gap-1">
            <Briefcase size={11} weight="duotone" />
            {thread.case.caseNumber}
          </Badge>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-1 overflow-y-auto px-5 py-4">
        {messagesQ.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CircleNotch size={14} className="animate-spin" /> Loading...
          </div>
        )}
        {messagesQ.data?.map((m, idx) => {
          const prev = idx > 0 ? messagesQ.data?.[idx - 1] : null;
          const showDay = !prev || !sameDay(prev.createdAt, m.createdAt);
          const sameSenderAsPrev = prev && prev.senderUserId === m.senderUserId;
          const closeInTime =
            prev &&
            new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 5 * 60_000;
          const showHeader = !(sameSenderAsPrev && closeInTime && !showDay);
          const isMe = m.senderUserId === currentUser?.id;
          return (
            <div key={m.id}>
              {showDay && (
                <div className="my-3 flex items-center gap-2 text-center">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    {formatDay(m.createdAt)}
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              <MessageBubble m={m} isMe={isMe} showHeader={showHeader} />
            </div>
          );
        })}
        {messagesQ.data && messagesQ.data.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No messages yet. Start the conversation!
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSend}
        className="flex items-end gap-2 border-t border-border bg-background px-4 py-3"
      >
        <textarea
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Write a message — Enter to send, Shift+Enter for newline"
          rows={2}
          maxLength={4000}
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-600"
        />
        <Button
          type="submit"
          disabled={!composer.trim() || sendMut.isPending}
          className="h-9 gap-1.5 px-3"
        >
          {sendMut.isPending ? (
            <CircleNotch size={14} className="animate-spin" />
          ) : (
            <PaperPlaneTilt size={14} weight="fill" />
          )}
          Send
        </Button>
      </form>
    </Card>
  );
}

function MessageBubble({
  m,
  isMe,
  showHeader,
}: {
  m: PortalMessage;
  isMe: boolean;
  showHeader: boolean;
}) {
  const sender = m.senderUser
    ? `${m.senderUser.firstName} ${m.senderUser.lastName}`
    : 'Unknown';
  const hasAttachments = m.attachments && m.attachments.length > 0;

  return (
    <div
      className={cn(
        'group flex flex-col',
        isMe ? 'items-end' : 'items-start',
        showHeader ? 'mt-3 first:mt-0' : 'mt-0.5',
      )}
    >
      {showHeader && (
        <div className="mb-1 text-xs uppercase tracking-[0.06em] text-muted-foreground">
          {isMe ? 'You' : sender} · {formatTime(m.createdAt)}
        </div>
      )}
      <div
        className={cn(
          'relative max-w-[78%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm leading-relaxed',
          isMe ? 'bg-brand-600 text-white' : 'bg-muted text-foreground',
        )}
      >
        {m.body}
        {hasAttachments && (
          <div
            className={cn(
              'mt-2 space-y-1',
              isMe ? 'border-t border-white/20 pt-2' : 'border-t border-border pt-2',
            )}
          >
            {m.attachments!.map((att) => (
              // The chip used to be an inert <div>: counsel could attach a document and the
              // solicitor saw only its filename, with no way to open it. Served through the
              // portal's own authenticated download route (scoped to their instructed matters).
              <button
                key={att.id}
                type="button"
                onClick={() => {
                  void downloadSolicitorDocument(att.documentId, att.document.name).catch(() =>
                    toast.error(`Could not open ${att.document.name}`),
                  );
                }}
                className={cn(
                  'flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs transition',
                  isMe ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-background/60 text-foreground hover:bg-accent',
                )}
              >
                <File size={12} weight="duotone" />
                <span className="truncate max-w-[180px]">{att.document.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Helpers
function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d.toISOString(), today.toISOString())) return 'Today';
  if (sameDay(d.toISOString(), yesterday.toISOString())) return 'Yesterday';
  return d.toLocaleDateString([], {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
  });
}

function sameDay(aIso: string, bIso: string): boolean {
  const a = new Date(aIso);
  const b = new Date(bIso);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
